import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, Plus, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { AdminHeader } from "@/components/platform/AdminHeader";
import { Count } from "@/components/platform/editor/EditorHome";

const EMPTY = { title: "", slug: "", body: "", show_in_footer: true, published: true, meta_title: "", meta_description: "" };
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

function PageDialog({ page, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (page) setF({ ...EMPTY, ...page }); }, [page]);
  if (!page) return null;
  const builtin = page.builtin;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const seo = { meta_title: f.meta_title.trim(), meta_description: f.meta_description.trim() };
      if (builtin) await api.put(`/asv/admin/editor/pages/builtin/${page.id}`, seo);
      else {
        const body = { ...f, ...seo, title: f.title.trim(), slug: f.slug || slugify(f.title) };
        delete body.id; delete body.path; delete body.created_at; delete body.updated_at;
        if (page.id) await api.put(`/asv/admin/editor/pages/${page.id}`, body); else await api.post("/asv/admin/editor/pages", body);
      }
      toast.success("Page saved"); onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-2xl border-slate-800 bg-[#0B1220] text-slate-200" data-testid="page-dialog">
        <DialogHeader><DialogTitle className="text-white">{builtin ? `${page.title} · SEO` : page.id ? "Edit page" : "New page"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!builtin && <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} maxLength={120} onChange={(e) => setF({ ...f, title: e.target.value, slug: page.id ? f.slug : slugify(e.target.value) })} data-testid="page-title" /></div>
              <div className="space-y-1.5"><Label>Link</Label><div className="flex items-center gap-1 text-sm text-slate-500">/<Input value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} data-testid="page-slug" /></div></div>
            </div>
            <div className="space-y-1.5"><Label>Content</Label><Textarea rows={10} value={f.body} onChange={set("body")} placeholder={"Write the page text. Start a line with # for a heading. Leave an empty line between paragraphs."} data-testid="page-body" /></div>
            <div className="flex flex-wrap gap-6 text-sm">
              <label className="flex items-center gap-2"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="page-published" />Published</label>
              <label className="flex items-center gap-2"><Switch checked={f.show_in_footer} onCheckedChange={(v) => setF({ ...f, show_in_footer: v })} data-testid="page-footer" />Show in footer</label>
            </div></>}
          <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">SEO</p>
          <div className="space-y-1.5"><Label>Meta title <Count v={f.meta_title} max={60} /></Label><Input value={f.meta_title} onChange={set("meta_title")} data-testid="page-meta-title" /></div>
          <div className="space-y-1.5"><Label>Meta description <Count v={f.meta_description} max={160} /></Label><Textarea rows={2} value={f.meta_description} onChange={set("meta_description")} data-testid="page-meta-description" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={save} data-testid="page-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ p, onEdit, onDelete }) => (
  <div className="flex flex-wrap items-center gap-3 border-t border-slate-800 py-3" data-testid={`page-row-${p.id}`}>
    <div className="min-w-0 flex-1"><p className="font-semibold text-white">{p.title} {p.builtin && <span className="ml-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase text-slate-400">Built-in</span>}{p.published === false && <span className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] uppercase text-amber-300">Draft</span>}</p>
      <p className="text-xs text-slate-500">{p.path}{p.meta_title ? ` · ${p.meta_title}` : ""}</p></div>
    <a href={p.path} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white" title="Open"><ExternalLink className="h-4 w-4" /></a>
    <button type="button" onClick={onEdit} className="text-slate-300 hover:text-white" title={p.builtin ? "Edit SEO" : "Edit"} data-testid={`page-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
    {!p.builtin && <button type="button" onClick={onDelete} className="text-rose-300 hover:text-rose-200" title="Remove" data-testid={`page-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
  </div>
);

export function EditorPages() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/asv/admin/editor/pages").then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => { load(); }, [load]);
  const del = (p) => window.confirm(`Remove the page "${p.title}"?`) && api.delete(`/asv/admin/editor/pages/${p.id}`).then(() => { toast.success("Page removed"); load(); }).catch((e) => toast.error(formatErr(e)));
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />;
  return (
    <div data-testid="editor-pages">
      <AdminHeader title="Pages" sub="Add your own pages (e.g. About, Terms, Privacy) and set SEO for every page.">
        <Button className="rounded-full" onClick={() => setEdit({ ...EMPTY })} data-testid="page-add"><Plus className="h-4 w-4" />New page</Button>
      </AdminHeader>
      <div className="card-surface p-5"><p className="font-semibold text-white">Your pages</p>
        {d.custom.map((p) => <Row key={p.id} p={p} onEdit={() => setEdit(p)} onDelete={() => del(p)} />)}
        {!d.custom.length && <p className="pt-2 text-sm text-slate-500" data-testid="pages-empty">No pages yet.</p>}</div>
      <div className="card-surface mt-4 p-5"><p className="font-semibold text-white">Site pages</p><p className="text-xs text-slate-500">These pages are part of the site. You can set their SEO title and description.</p>
        {d.builtin.map((p) => <Row key={p.id} p={p} onEdit={() => setEdit(p)} />)}</div>
      <PageDialog page={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
